/**
 * Detalle de un ítem del corpus PEFF. Los campos LINGÜÍSTICOS son solo lectura
 * (editarlos exige capa de validación clínica — PR aparte, regla de oro #3). Lo
 * único editable es la sección "Medios del estímulo" (imagen + audio): son
 * estímulos de PRESENTACIÓN, no datos que alimenten el scoring.
 */
import { Link, useParams } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import { nombreProceso } from "./procesos.js";
import { MediosEstimulo } from "./MediosEstimulo.js";
import type { CorpusTipoMedia } from "../../api/types.js";
import s from "./admin.module.css";

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className={s.dt}>{label}</div>
      <div className={s.dd}>{children}</div>
    </div>
  );
}

export function AdminBancoDetail() {
  const { id = "" } = useParams();
  const q = useQuery(() => adminApi.wordBankCorpusItem(id), [id]);
  const item = q.data;

  return (
    <>
      <Link to="/admin/banco" className={s.backLink}>
        ‹ Volver a Banco de Palabras
      </Link>

      {q.loading && !item ? (
        <div className={s.empty}>Cargando…</div>
      ) : q.error ? (
        <div className={`${s.banner} ${s.bannerError}`}>{q.error}</div>
      ) : !item ? (
        <div className={s.empty}>Ítem no encontrado.</div>
      ) : (
        <>
          <div className={s.sectionTitle}>{item.palabra}</div>

          {item.pendienteValidacionClinica ? (
            <div className={`${s.banner} ${s.bannerInfo}`}>
              Ítem ◊ pendiente de validación clínica: se administra pero <strong>no puntúa</strong>.
            </div>
          ) : null}

          <div className={`${s.panel} ${s.panelPad}`}>
            <div className={s.dl}>
              <Campo label="Batería">
                {item.tipoBateria === "fonologica" ? "Fonológica (PSF)" : "Fonética"}
              </Campo>
              <Campo label="Ejercicio">{EJERCICIO_LABEL[item.corpusTipo]}</Campo>
              <Campo label="Estado">
                {item.estado === "revision" ? "Revisión clínica" : "Activo"}
              </Campo>
              {item.corpusTipo === "psf" ? (
                <Campo label="Proceso principal">{nombreProceso(item.procesoPrincipal)}</Campo>
              ) : item.corpusTipo === "fonetica_palabra" ? (
                <Campo label="Posición">
                  <span className={s.mono}>{item.posicion ?? "—"}</span>
                </Campo>
              ) : item.corpusTipo === "oracion" ? (
                <Campo label="Fonema objetivo">
                  <span className={s.mono}>{item.procesoPrincipal ?? "—"}</span>
                </Campo>
              ) : null}
              <Campo label="Edad de exigencia">
                {item.edadExigencia !== null ? `${item.edadExigencia} años` : "—"}
              </Campo>
              {item.banda ? <Campo label="Banda">{item.banda}</Campo> : null}
              <Campo label="Dialecto">{item.dialecto}</Campo>
              {item.orden !== null ? <Campo label="Orden">{item.orden}</Campo> : null}
              {item.estructuraSilabica ? (
                <Campo label="Estructura silábica">
                  <span className={s.mono}>{item.estructuraSilabica}</span>
                </Campo>
              ) : null}
              {item.titular2 ? <Campo label="Segundo titular">{item.titular2}</Campo> : null}
            </div>
          </div>

          {item.procesosSecundarios.length > 0 ? (
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Procesos secundarios</div>
              <div className={s.btnRow} style={{ marginTop: 6 }}>
                {item.procesosSecundarios.map((c) => (
                  <span key={c} className={`${s.badge} ${s.badgeNeutral}`}>
                    {nombreProceso(c)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {item.alternativasRotacion.length > 0 ? (
            <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
              <div className={s.dt}>Alternativas de rotación (re-test banda D)</div>
              <div className={s.dd}>{item.alternativasRotacion.join(", ")}</div>
            </div>
          ) : null}

          <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
            <div className={s.dl}>
              <Campo label="Pista semántica">{item.pistaSemantica ?? "—"}</Campo>
              <Campo label="Pregunta distractora">{item.preguntaDistractora ?? "—"}</Campo>
              <Campo label="Notas clínicas">
                {item.notasClinicas ?? <span className={s.muted}>—</span>}
              </Campo>
            </div>
          </div>

          <MediosEstimulo corpusTipo={tipoMedia(item.id)} corpusRef={refDe(item.id)} />
        </>
      )}
    </>
  );
}

/** Etiqueta de ejercicio por tabla del corpus. */
const EJERCICIO_LABEL: Record<CorpusTipoMedia, string> = {
  silaba: "F1 · sílabas",
  fonetica_palabra: "F2/F3 · denominación",
  oracion: "F4 · oraciones",
  psf: "PSF1/PSF2 · fonológica",
};

/** Deriva (corpusTipo, corpusRef) del id compuesto del visor
 *  ("psf:" | "fon:" | "sil:" | "ora:" + uuid). */
const PREFIJO_TIPO: Record<string, CorpusTipoMedia> = {
  psf: "psf",
  fon: "fonetica_palabra",
  sil: "silaba",
  ora: "oracion",
};
function tipoMedia(id: string): CorpusTipoMedia {
  return PREFIJO_TIPO[id.slice(0, 3)] ?? "psf";
}
function refDe(id: string): string {
  return id.replace(/^(psf|fon|sil|ora):/, "");
}
